import React from 'react';
import { Collapse, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
    KeyboardArrowDown,
    KeyboardArrowRight,
    VisibilityOffOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import styles from './CollapseComponent.module.scss';
import type { IIndexable } from '../../../../types';
import type { Core } from '@pdftron/webviewer';
import Icon from '@mdi/react';
import { mdiFolderTable, mdiTable, mdiTableLock, mdiTableMultiple } from '@mdi/js';

interface ICollapseComponentProps {
    level: number;
    altTitle: string;
    layerNodes: Core.Document.LayerContext[];
    expanded: IIndexable<boolean>;
    toggleExpanded: (key: string) => void;
    onLayersChanged: () => void;
}

/* Returns true, if the node is a OCG (Optional Content Group), otherwise false. */
const isOCG = (node: Core.Document.LayerContext): boolean => {
    return node['visible'] === undefined;
};

const isGroupLayer = (node: Core.Document.LayerContext): boolean => {
    return !isOCG(node) && node.children?.length > 0;
};

const CollapseComponent = (props: ICollapseComponentProps) => {
    const onToggleVisibility = (node: Core.Document.LayerContext) => {
        const newVisible = !node.visible;
        setNestedVisibility(node, newVisible);

        props.onLayersChanged();
    };

    const setNestedVisibility = (node: Core.Document.LayerContext, newValue: boolean) => {
        if (!isOCG(node)) {
            node.visible = newValue;
        }

        if (node.children?.length) {
            node.children.forEach(nestedNode => setNestedVisibility(nestedNode, newValue));
        }
    };

    const renderTypeIcon = (node: Core.Document.LayerContext) => {
        let icon = mdiTable;

        if (isOCG(node)) {
            icon = mdiFolderTable;
        } else if (isGroupLayer(node)) {
            icon = mdiTableMultiple;
        } else if (node.locked) {
            icon = mdiTableLock;
        }

        return <Icon path={icon} size={1} />;
    };

    const renderVisibilityIcon = (node: Core.Document.LayerContext) => {
        if (isOCG(node)) {
            return <></>;
        }

        return (
            <IconButton disabled={node.locked} onClick={() => onToggleVisibility(node)}>
                {node.visible ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
            </IconButton>
        );
    };

    const renderCollapseIcon = (node: Core.Document.LayerContext) => {
        if (!node?.children?.length) {
            return null;
        }

        return (
            <ListItemIcon>
                {props.expanded[node.obj] ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </ListItemIcon>
        );
    };

    const listElements = () => {
        const elements: JSX.Element[] = [];

        props.layerNodes.forEach((node, idx) => {
            elements.push(
                <ListItem
                    className={styles.sidebarMainListItem}
                    key={`listButton-${idx}`}
                    onClick={() => node.children?.length && props.toggleExpanded(node.obj)}
                    secondaryAction={renderVisibilityIcon(node)}
                >
                    {renderCollapseIcon(node)}
                    <ListItemIcon>{renderTypeIcon(node)}</ListItemIcon>
                    <ListItemText primary={node.name ? node.name : '<>'} />
                </ListItem>,
            );

            if (node.children.length > 0) {
                elements.push(
                    <Collapse
                        key={`listCollapse-${idx}`}
                        in={props.expanded[node.obj]}
                        timeout="auto"
                        unmountOnExit
                    >
                        <List component="div" disablePadding>
                            <CollapseComponent
                                level={props.level + 1}
                                altTitle=""
                                layerNodes={[...node.children]}
                                expanded={props.expanded}
                                toggleExpanded={props.toggleExpanded}
                                onLayersChanged={props.onLayersChanged}
                            />
                        </List>
                    </Collapse>,
                );
            }
        });
        return elements;
    };

    return (
        <List className={props.level === 0 ? styles.resultList : styles.resultListChild}>
            {listElements()}
        </List>
    );
};

export default CollapseComponent;
